import { payPointsKeys } from "Api/Queries/PayPoints/usePayPointsQuery";
import type {
  CreateInternalPayPointRequest,
  ErrorResponse,
  InternalPayPointDto,
} from "Infrastructure/Api/Api";
import {
  postPayPoints,
  postPayPointsMerchantMerchantPublicID,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Translations/Resources";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

type Props = {
  onSuccess?: (paypointPublicID: string) => void;
  onError?: (error: ErrorResponse) => void;
  merchantPublicID?: string;
};

export const useCreatePayPoint = ({
  onSuccess,
  onError,
  merchantPublicID,
}: Props = {}) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation<
    InternalPayPointDto,
    ErrorResponse,
    CreateInternalPayPointRequest
  >({
    mutationFn: processResponseWithApiProblemDetails(
      (request: CreateInternalPayPointRequest) =>
        merchantPublicID
          ? postPayPointsMerchantMerchantPublicID(request, merchantPublicID)
          : postPayPoints(request),
    ),
    onSuccess: (data, { name }) => {
      queryClient.invalidateQueries({
        queryKey: payPointsKeys.all,
      });
      toast.success(t(Resources.Hooks.Paypoint.PaypointCreated, { name }));
      onSuccess?.(data.publicID);
    },
    onError: (error: ErrorResponse) => {
      toast.error(t(Resources.Hooks.Paypoint.PaypointCreateError));
      onError?.(error);
    },
  });
};
